import { useLogger } from '@common/core-layer/composables/useLogger'
import { clearAuthCookie, getAuthCookie, setAuthCookie } from '@common/core-layer/utils/cookie.utils'
import { getRefreshTrigger, performRefresh, type RefreshOutcome } from '@auth/utils/ssr-token-refresh.utils'
import { trackRefreshFailureAutoLogout } from '@common/analytics-layer/utils/track-auto-logout.utils'
import {
  fingerprintRefreshToken,
  resolveAutoLogoutDevice,
  resolveAutoLogoutFlavour,
} from '@common/analytics-layer/tracking/auto-logout-envelope'
import type { AuthCookieData } from '@common/core-layer/utils/auth-cookie.utils'

/**
 * Proactive client boot token refresh — registered BEFORE init-location in
 * `nuxt.base.config.ts` so no app API call ever fires with an expired token.
 *
 * Uses the same context-free refresh util as the SSR middleware (cookie carries
 * `locationId`; URL per `APP_NAME`), because the location store does not exist
 * yet at this point. Meet is skipped inside the util (Auth V1/V2 needs the
 * location store).
 *
 * Blocking only when the access token is EXPIRED (it is useless otherwise).
 * Near-expiry tokens still authorize this page load, so the refresh runs in
 * the background and the boot never waits on it.
 *
 * Terminal failures (4xx — refresh token rejected) clear the dead session so
 * the app does not boot looking authenticated. Transient failures leave the
 * cookie untouched — the BaseService 401 safety net owns recovery.
 *
 * SSR is covered by `libs/middleware/auth.ts`; this plugin is client-only.
 *
 * A terminal outcome here is a real auto-logout that reaches NEITHER `useAuth.refreshToken()` nor
 * the SSR reporter, so it is reported separately with `origin: 'client-boot'`. It is likely the
 * highest-volume case: this plugin runs before any app API call, so once it clears the cookie the
 * BaseService 401 path never fires with the stale token.
 */
/** Cookie-jar boundary — injected so unit tests run without vi.mock on cross-layer utils. */
export interface AuthRefreshBootDeps {
  getAuthCookie: () => AuthCookieData | null
  setAuthCookie: (data: AuthCookieData) => void | Promise<void>
  clearAuthCookie: () => void
}

/**
 * Reports the boot-time auto-logout before the cookie is cleared, since the clear removes the
 * identity and refresh token the event describes.
 *
 * Fully self-guarded: analytics must never stop the dead session from being cleared. `flavour` and
 * `device` read ambient context that can be unavailable this early, so each degrades to a default
 * rather than costing the whole diagnostic.
 */
function reportBootAutoLogout(authData: AuthCookieData, outcome: { status?: number; error?: string }): void {
  try {
    trackRefreshFailureAutoLogout({
      origin: 'client-boot',
      locationId: authData.locationId,
      contactId: authData.contactId,
      flavour: resolveAutoLogoutFlavour(process.env.APP_NAME),
      device: resolveAutoLogoutDevice(typeof navigator === 'undefined' ? undefined : navigator.userAgent),
      refresh: {
        ...(outcome.status === undefined ? {} : { statusCode: outcome.status }),
        error: outcome.error ?? 'Boot refresh rejected',
        fingerprint: fingerprintRefreshToken(authData.refreshToken),
      },
    })
  } catch (error) {
    const { logger } = useLogger()
    logger.warn('[Auth-Refresh] Auto-logout analytics emit failed; continuing to clear the session', {
      labels: { context: 'plugins/auth-refresh', phase: 'analytics' },
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }
}

async function applyBootOutcome(outcome: RefreshOutcome, deps: AuthRefreshBootDeps, authData: AuthCookieData): Promise<void> {
  const { logger } = useLogger()
  if (outcome.kind === 'success') {
    await deps.setAuthCookie(outcome.auth)
    logger.info('[Auth-Refresh] Boot token refresh succeeded', {
      labels: { context: 'plugins/auth-refresh' },
    })
  } else if (outcome.kind === 'terminal') {
    reportBootAutoLogout(authData, outcome)
    deps.clearAuthCookie()
    logger.warn('[Auth-Refresh] Boot refresh rejected (4xx); cleared dead session', {
      labels: { context: 'plugins/auth-refresh', outcome: 'session-cleared' },
    })
  }
}

export async function runAuthRefreshBoot(deps: AuthRefreshBootDeps): Promise<void> {
  const { logger } = useLogger()

  try {
    const authData = deps.getAuthCookie()
    if (!authData?.token) {
      return
    }
    const trigger = getRefreshTrigger(authData.token)
    if (!trigger) {
      return
    }

    const refresh = performRefresh(authData)
    if (trigger === 'near-expiry') {
      // Fire-and-forget is the point: boot must not wait on a still-valid token.
      // eslint-disable-next-line promise/prefer-await-to-then
      void refresh.then(outcome => applyBootOutcome(outcome, deps, authData))
      return
    }
    await applyBootOutcome(await refresh, deps, authData)
  } catch (error) {
    logger.warn('[Auth-Refresh] Boot token refresh errored; continuing with existing cookie', {
      labels: { context: 'plugins/auth-refresh' },
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }
}

export default defineNuxtPlugin(async () => {
  const { logger } = useLogger()
  logger.info('[Auth-Refresh] Plugin invoked at client boot', {
    labels: { context: 'plugins/auth-refresh', phase: 'invoke' },
  })
  await runAuthRefreshBoot({ getAuthCookie, setAuthCookie, clearAuthCookie })
})
